import React from 'react';
import { Box } from '@mui/material';
import {
  signalBorders,
  signalBordersLight,
  signalColors,
  signalColorsOnLight,
  signalFonts,
  signalGradients,
  signalRadii,
  signalShadows,
  signalSurfaces,
  signalSurfacesLight,
  signalText,
  signalTextLight,
} from '@/theme/foundations/tokens';
import {
  Mono,
  ProgressTrack,
  ScreenFrame,
  SectionLabel,
} from '@/common/components/signal';
import { download, uploads } from '../fixtures';
import type { TransferFixture } from '../fixtures';

/**
 * Every color the drawer needs, resolved once per mode.
 *
 * The design draws this panel twice — dark and light — and the light version
 * is not a filter over the dark one: the accent darkens to #C74806 and the cyan
 * to #1E88A8 so both keep their contrast against a near-white fill. Holding the
 * two sets side by side here is what lets one component render both.
 */
interface DrawerPalette {
  surface: string;
  border: string;
  divider: string;
  dashed: string;
  shadow: string;
  text: string;
  body: string;
  muted: string;
  faint: string;
  up: string;
  upBar: string;
  down: string;
  downBar: string;
  ok: string;
  fail: string;
  track: string;
}

const DARK: DrawerPalette = {
  surface: signalSurfaces.glassDrawer,
  border: 'rgba(255,255,255,0.11)',
  divider: signalBorders.divider,
  dashed: signalBorders.strong,
  shadow: signalShadows.drawer,
  text: signalText.primary,
  body: signalText.secondary,
  muted: signalText.muted,
  faint: signalText.faint,
  up: signalColors.accentBright,
  upBar: signalGradients.accentBar,
  down: signalColors.cyan,
  downBar: signalGradients.cyanBar,
  ok: signalColors.green,
  fail: signalColors.red,
  track: signalSurfaces.track,
};

const LIGHT: DrawerPalette = {
  surface: signalSurfacesLight.glassDrawer,
  border: signalBordersLight.strong,
  divider: signalBordersLight.divider,
  dashed: signalBordersLight.dashed,
  shadow: '0 40px 90px -34px rgba(0,0,0,0.45)',
  text: signalTextLight.primary,
  body: signalTextLight.secondary,
  muted: signalTextLight.muted,
  faint: signalTextLight.faint,
  up: signalColorsOnLight.accent,
  upBar: signalGradients.accentBarLight,
  down: signalColorsOnLight.cyan,
  downBar: signalColorsOnLight.cyan,
  ok: signalColorsOnLight.green,
  fail: signalColorsOnLight.red,
  track: signalSurfacesLight.track,
};

/** One in-flight transfer: label, percentage, bar, bytes and ETA. */
const TransferRow: React.FC<{
  transfer: TransferFixture;
  icon: string;
  color: string;
  bar: string;
  p: DrawerPalette;
}> = ({ transfer, icon, color, bar, p }) => (
  <Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '7px',
      }}
    >
      <Box component="span" sx={{ color, fontSize: '11px' }}>
        {icon}
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: '12.5px',
          color: p.text,
          flex: 1,
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {transfer.name}
      </Box>
      <Mono size={10.5} weight={600} color={color}>
        {transfer.pct}%
      </Mono>
    </Box>

    <ProgressTrack value={transfer.pct} fill={bar} track={p.track} />

    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '6px',
      }}
    >
      <Mono size={10} color={p.muted}>
        {transfer.sub}
      </Mono>
      <Mono size={10} color={p.muted}>
        {transfer.eta}
      </Mono>
    </Box>
  </Box>
);

const history = [
  { icon: '✓', label: 'Nightdrive v13 pushed', right: '12:04', bad: false },
  { icon: '✓', label: 'Samples/Imported (286)', right: '11:58', bad: false },
  { icon: '!', label: 'Halcyon — conflict on .flp', right: 'RETRY', bad: true },
];

const TransferDrawer: React.FC<{ light?: boolean }> = ({ light = false }) => {
  const p = light ? LIGHT : DARK;

  return (
    <Box
      sx={{
        width: 400,
        height: 720,
        maxWidth: '100%',
        borderRadius: `${signalRadii['3xl']}px`,
        border: `1px solid ${p.border}`,
        background: p.surface,
        backdropFilter: 'blur(24px)',
        boxShadow: p.shadow,
        color: p.text,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          padding: '18px 20px 14px',
          borderBottom: `1px solid ${p.divider}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            component="h3"
            sx={{
              margin: 0,
              fontFamily: signalFonts.display,
              fontWeight: 700,
              fontSize: '18px',
              letterSpacing: '-0.02em',
              color: p.text,
            }}
          >
            Transfers
          </Box>
          <Mono size={11} color={p.muted}>
            ✕
          </Mono>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            marginTop: '12px',
          }}
        >
          <Mono size={11} color={p.up}>
            ↑ 18.4 MB/s
          </Mono>
          <Mono size={11} color={p.down}>
            ↓ 4.1 MB/s
          </Mono>
          <Mono size={11} color={p.muted} sx={{ marginLeft: 'auto' }}>
            3 ACTIVE · 1 QUEUED
          </Mono>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          padding: '16px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        <Box>
          <SectionLabel
            size={10}
            color={p.muted}
            sx={{ display: 'block', marginBottom: '11px' }}
          >
            UPLOADING
          </SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {uploads.map((t) => (
              <TransferRow
                key={t.name}
                transfer={t}
                icon="↑"
                color={p.up}
                bar={p.upBar}
                p={p}
              />
            ))}
          </Box>
        </Box>

        <Box>
          <SectionLabel
            size={10}
            color={p.muted}
            sx={{ display: 'block', marginBottom: '11px' }}
          >
            DOWNLOADING
          </SectionLabel>
          <TransferRow
            transfer={download}
            icon="↓"
            color={p.down}
            bar={p.downBar}
            p={p}
          />
        </Box>

        <Box>
          <SectionLabel
            size={10}
            color={p.muted}
            sx={{ display: 'block', marginBottom: '11px' }}
          >
            QUEUED
          </SectionLabel>
          {/* Dashed rather than solid: nothing is moving on this row yet. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 12px',
              border: `1px dashed ${p.dashed}`,
              borderRadius: `${signalRadii.lg}px`,
            }}
          >
            <Box component="span" sx={{ color: p.muted, fontSize: '11px' }}>
              ⏸
            </Box>
            <Box sx={{ fontSize: '12.5px', color: p.body, flex: 1 }}>
              Halcyon — Master v3.wav
            </Box>
            <Mono size={10} color={p.muted}>
              96 MB
            </Mono>
          </Box>
        </Box>

        <Box sx={{ marginTop: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '11px',
            }}
          >
            <SectionLabel size={10} color={p.muted}>
              SESSION HISTORY
            </SectionLabel>
            <Mono size={10} color={p.faint}>
              CLEAR
            </Mono>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
            {history.map((h) => (
              <Box
                key={h.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '9px',
                  fontSize: '12px',
                  color: p.body,
                }}
              >
                <Box
                  component="span"
                  sx={{ color: h.bad ? p.fail : p.ok, fontSize: '11px' }}
                >
                  {h.icon}
                </Box>
                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {h.label}
                </Box>
                <Mono
                  size={10}
                  weight={h.bad ? 600 : 500}
                  color={h.bad ? p.fail : p.muted}
                >
                  {h.right}
                </Mono>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * 04 — Transfer sidebar.
 *
 * Ordered by how much attention each band deserves: what is moving now, what is
 * waiting, then what already happened. The failed history row is the only red
 * on the panel and carries its own RETRY, so a problem is visible without
 * having to scan the list.
 */
export const Screen04Transfers: React.FC = () => (
  <ScreenFrame index="04" title="Transfer sidebar" note="DARK · LIGHT" bare>
    <Box sx={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
      <TransferDrawer />
      <TransferDrawer light />
    </Box>
  </ScreenFrame>
);

export default Screen04Transfers;
